package dev.lingvo.site.ds

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import dev.lingvo.site.i18n.tf
import kotlin.random.Random
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.disabled
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Text

/**
 * A write-only credential field whose reveal control has an exact accessible name.
 *
 * Password is the default on every composition. Revealing changes only the native input type; it
 * never copies the secret into another node. Browser assistance is deliberately constrained for
 * API credentials rather than account passwords.
 */
@Composable
fun LSecretField(
    value: String,
    onValueChange: (String) -> Unit,
    modifier: Modifier = Modifier,
    label: String,
    placeholder: String = "",
    error: String? = null,
    enabled: Boolean = true,
    revealLabel: String = tf("ds.ds.lsecret_field.show_label", "Show {label}", "label" to label),
    concealLabel: String = tf("ds.ds.lsecret_field.hide_label", "Hide {label}", "label" to label),
) {
    val inputId = remember { "lingvo-secret-${Random.nextInt(1_000_000)}" }
    val errorId = "$inputId-error"
    var revealed by remember { mutableStateOf(false) }
    val state = if (revealed) "revealed" else "concealed"

    Column(
        LFieldGroupStyle.toModifier()
            .then(modifier)
            .attr("data-secret-field", "")
            .attr("data-state", state)
            .attr("data-disabled", (!enabled).toString()),
    ) {
        Label(forId = inputId, attrs = LFieldLabelStyle.toModifier().toAttrs()) {
            Text(label)
        }
        Row(
            LTextFieldFrameStyle.toModifier()
                .attr("data-invalid", (error != null).toString()),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Input(
                type = if (revealed) InputType.Text else InputType.Password,
                attrs = LTextFieldInputStyle.toModifier().toAttrs {
                    id(inputId)
                    value(value)
                    placeholder(placeholder)
                    attr("data-secret-input", "")
                    attr("autocomplete", "new-password")
                    attr("autocapitalize", "none")
                    attr("autocorrect", "off")
                    attr("spellcheck", "false")
                    if (!enabled) disabled()
                    if (error != null) {
                        attr("aria-invalid", "true")
                        attr("aria-describedby", errorId)
                    }
                    onInput { onValueChange(it.value) }
                },
            )
            LIconButton(
                label = if (revealed) concealLabel else revealLabel,
                onClick = { revealed = !revealed },
                modifier = Modifier
                    .attr("data-secret-reveal", "")
                    .attr("data-state", state),
                enabled = enabled,
            ) {
                if (revealed) LEyeOffIcon() else LEyeIcon()
            }
        }
        if (error != null) {
            Div(LFieldErrorStyle.toModifier().toAttrs {
                id(errorId)
                attr("role", "alert")
            }) {
                Text(error)
            }
        }
    }
}
